import { Button } from '@design-system';
import { StripeEmbeddedCheckout, type CheckoutPlanContent } from '@/features/advisor-onboarding';

import { Check, ChevronLeft, Lock, Shield, Info, Bell } from 'lucide-react';

interface CheckoutLayoutProps {
  content: CheckoutPlanContent;
  trialDetailFormatted: string | null;
  onBack: () => void;
  isLoading: boolean;
  clientSecret: string | null;
}

export function CheckoutLayout({
  content,
  trialDetailFormatted,
  onBack,
  isLoading,
  clientSecret,
}: CheckoutLayoutProps) {
  const showTrialBox = content.hasTrial && trialDetailFormatted;

  return (
    <div className="flex min-h-screen flex-col items-center p-6 md:p-8">
      <div className="w-full max-w-6xl">
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left Column - Plan & Trial */}
          <div className="space-y-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={onBack}
            >
              <ChevronLeft />
              Back to plans
            </Button>
            <h1 className="mb-6 text-2xl font-bold text-gray-950">{content.displayName}</h1>
            {/* Featured plan card - purple border & light purple bg */}
            <div className="border-primary bg-primary/10 rounded-lg border-2 p-6">
              <h2 className="text-3xl font-bold text-gray-950">{content.headline}</h2>
              <p className="text-foreground mt-1 text-base">{content.subhead}</p>
              <ul className="mt-3 space-y-2">
                {content.features.map(feature => (
                  <li
                    key={feature}
                    className="text-foreground flex items-center gap-2 text-sm"
                  >
                    <Check className="text-primary size-5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trial detail box */}
            {showTrialBox ? (
              <div className="space-y-4 rounded-lg bg-purple-50 p-5">
                {/* Main trial info */}
                <div className="flex gap-3">
                  <Shield className="text-primary mt-0.5 size-5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-950">
                      {content.trialDays}-day free trial — full access
                    </p>
                    <p className="text-foreground mt-1 text-sm leading-relaxed">
                      {trialDetailFormatted}
                    </p>
                  </div>
                </div>

                {/* Reminder info */}
                <div className="flex gap-3">
                  <Bell className="text-primary mt-0.5 size-5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-foreground text-sm leading-relaxed">
                      Don't worry — we'll email you 2-3 days before your trial ends so you can
                      cancel if it's not a fit.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 rounded-lg bg-purple-50 p-5">
                {/* Main trial info */}
                <div className="flex gap-3">
                  <Info className="text-primary mt-0.5 size-5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-950">Paid plan — full access</p>
                    <p className="text-foreground mt-1 text-sm leading-relaxed">
                      You can always start with a two-weeks free trial before you&apos;re billed.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Payment */}
          <div>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className=" bg-white px-6 py-4">
                <div className="flex items-center gap-2">
                  <Lock className="text-foreground size-5" />
                  <span className="font-semibold text-gray-950">Enter payment details</span>
                </div>
                <p className="text-foreground mt-1 text-sm">{content.paymentSubtitle}</p>
              </div>
              <div className="min-h-105 flex flex-col bg-white">
                {isLoading ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
                    <div className="border-primary h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
                    <p className="text-foreground font-medium">Preparing secure checkout...</p>
                  </div>
                ) : clientSecret ? (
                  <div className="w-full">
                    <StripeEmbeddedCheckout clientSecret={clientSecret} />
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-center px-6 py-16 text-center">
                    <p className="text-destructive font-medium">
                      Checkout session link expired or not found. Please go back and select a plan
                      again.
                    </p>
                  </div>
                )}
              </div>
              <div className=" bg-white px-6 py-4">
                <p className="text-foreground text-center text-xs">
                  By subscribing, you authorize Waterlily to charge you according to the terms until
                  you cancel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
